import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Menu, MenuItem } from "@mui/material";
import { useProductContext } from "../context";
import { useGlobalContext } from "context/GlobalContext";
import { PageTitle } from "components/PageMeta/PageTitle";
import { pageName } from "constants/pageNames";
import { productListColumnsS } from "../responsiveData/productListColumnsS";
import { productListColumnsL } from "../responsiveData/productListColumnsL";
import { ProductTopBanner } from "../components/ProductTopBanner";
import { confirmAction } from "utils/functions/confirmAction";
import { message } from "constants/responses/message";
import { deleteProductsApi } from "API/apis/product/deleteProducts";
import { STATUS_CODES } from "constants/responses/statusCodes";
import { routeLinks } from "constants/paths";
import { useNavigate } from "react-router-dom";
import { fetchProductsApi } from "API/apis/product/getProducts";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { productOperationsList } from "../constants/product.constants";
import { Box } from "@mui/system";
import dayjs from "configs/dayjs.config";

import { initiateTaskForExecutionTimelineApi } from "API/apis/product/initiateTaskForExecutionTimeline";
import { pmcAssignStatusApi } from "API/apis/product/pmcAssignStatus";
import { ReactTable } from "components/ReactTable/ReactTable";
import { useSearchParams } from "react-router-dom";
import { filterStringToObject } from "utils/functions/reactTableFilterConverter";

export const ProductList = () => {
  const [searchParams] = useSearchParams();
  const paramsToObject = Object.fromEntries(searchParams.entries());

  const navigate = useNavigate();
  const { projectId } = useParams();
  const {
    isMobile,
    setIsLoading,
    setSuccess,
    setError,
    setIsInitializing,
    setCopy,
    unBoundMode,
    pageAccess,
  } = useGlobalContext();
  const {
    setCurrentOperationPage,
    setPreviewProduct,
    currentProject,
    setProductsSelectedForTask,
    bdc,
    reloadRequired,
    setReloadRequired,
    selectedRowId,
    setSelectedRowId,
  } = useProductContext();
  const [list, setList] = useState([]);
  const currentUserId = useSelector((state) => state.user.state.userId);
  const [anchorElList, setAnchorElList] = useState(null);
  const openList = Boolean(anchorElList);

  useEffect(() => {
    setAllParams({
      ...paramsToObject,
      filters: filterStringToObject(paramsToObject.filters),
    });
  }, [searchParams]);

  const [allParams, setAllParams] = useState({
    ...paramsToObject,
    filters: filterStringToObject(paramsToObject.filters) || "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsInitializing(true);
        const { data, status } = await fetchProductsApi({
          projectId,
          canViewAll: pageAccess.canViewAll,
          ...allParams,
        });

        if (status !== 200) {
          throw new Error(message.error.generalFailure);
        }
        setList(data);
        setIsInitializing(false);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsInitializing(false);
        setIsLoading(false);
        setReloadRequired(false);
      }
    };
    fetchData();
  }, [reloadRequired, allParams]);

  const handleUpdateProducts = (row) => {
    navigate(
      routeLinks.projectManagementProduct + "/" + row.lotPartId + "/update"
    );
    setPreviewProduct(row);
    setCurrentOperationPage(productOperationsList.updateProduct);
  };

  const handlePreviewClick = (row) => {
    setPreviewProduct(row);
    navigate(routeLinks.projectManagementProduct + "/" + row.id + "/preview");
    setCurrentOperationPage(productOperationsList.previewProduct);
  };

  const handleInputOutputButtonClick = (row) => {
    navigate(
      routeLinks.projectManagementProduct +
        "/" +
        projectId +
        "/" +
        row.lotPartId +
        "/io"
    );
    setCurrentOperationPage(productOperationsList.productIoDetails);
  };

  const handleHandoverChecklistButtonClick = (row) => {
    navigate(
      routeLinks.projectManagementProduct +
        "/" +
        projectId +
        "/" +
        row.lotPartId +
        "/handover-checklist"
    );
    setCurrentOperationPage(productOperationsList.productIoDetails);
  };
  const handleTimelineButtonClick = (row) => {
    navigate(
      routeLinks.projectManagementProduct +
        "/" +
        projectId +
        "/" +
        row.lotPartId +
        "/preview-execution-timeline"
    );
    setCurrentOperationPage(productOperationsList.productIoDetails);
  };
  const handleDeleteClick = async ({ lotPartId }) => {
    try {
      const confirmed = confirmAction(message.info.productDeleteConfirmMessage);
      if (!confirmed) {
        return;
      }
      setIsLoading(true);
      const { data, status } = await deleteProductsApi(lotPartId);
      if (status !== STATUS_CODES.SUCCESS) {
        throw new Error(data.message);
      }
      setSuccess(data?.message);
      setIsLoading(false);
      setReloadRequired(true);
      navigate(routeLinks.projectManagementProject + "/" + currentProject);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleTaskButtonClick = () => {
    if (selectedRowId.length > 0) {
      setProductsSelectedForTask(selectedRowId);
      navigate(routeLinks.projectManagementProduct + "/task");
    } else {
      alert(message.error.noProductSelected);
    }
  };

  const handleAssignPMC = async () => {
    if (selectedRowId.length > 0) {
      setProductsSelectedForTask(selectedRowId);
      navigate(routeLinks.projectManagementProduct + "/assign-pmc");
    } else {
      alert(message.error.noProductSelected);
    }
  };

  const handlePMCChecklistButton = () => {
    if (selectedRowId.length > 0) {
      setProductsSelectedForTask(selectedRowId);
      navigate(
        routeLinks.projectManagementProduct +
          "/" +
          projectId +
          "/" +
          selectedRowId.map((row) => row.lotPartId).join(",") +
          "/pmc-checklist"
      );
    } else {
      alert(message.error.noProductSelected);
    }
  };

  const handleExecutionTimelineButton = () => {
    if (selectedRowId.length > 0) {
      setProductsSelectedForTask(selectedRowId);
      navigate(
        routeLinks.projectManagementProduct +
          "/" +
          projectId +
          "/" +
          selectedRowId.map((row) => row.lotPartId).join(",") +
          "/execution-timeline"
      );
    } else {
      alert(message.error.noProductSelected);
    }
  };

  const handleCopyChecklist = async (lotPartId) => {
    setCopy(lotPartId);
    setSuccess(message.info.copiedSuccessfully);
  };

  const handleCopyTimeline = async (lotPartId) => {
    setCopy(lotPartId);
    setSuccess(message.info.copiedSuccessfully);
  };

  const handleUpdateProductExecutionTimeline = (row) => {
    navigate(
      routeLinks.projectManagementProduct +
        "/" +
        projectId +
        "/" +
        row.lotPartId +
        "/edit-execution-timeline"
    );
  };

  const handleUpdateProductHandoverChecklist = (row) => {
    navigate(
      routeLinks.projectManagementProduct +
        "/" +
        projectId +
        "/" +
        row.lotPartId +
        "/pmc-checklist"
    );
  };

  const handleViewInputOutputClick = () => {
    return;
  };

  const handleApprovePMCClick = async (row, newStatus) => {
    try {
      setIsLoading(true);
      const { data, status } = await pmcAssignStatusApi({
        productLotProcessingId: row.lotPartId,
        status: newStatus,
      });
      if (status === 204) {
        throw new Error(message.info.noRecordsModified);
      }
      if (status !== STATUS_CODES.SUCCESS) {
        throw new Error(data.message);
      }
      setSuccess(`${data.message}`);
      setReloadRequired(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInitiateExecutionTimelineButton = async () => {
    if (selectedRowId.length > 0) {
      try {
        setIsLoading(true);
        const { data, status } = await initiateTaskForExecutionTimelineApi(
          selectedRowId.map((item) => item.lotPartId)
        );
        if (status === 204) {
          throw new Error(message.info.noRecordsModified);
        }
        if (status !== STATUS_CODES.SUCCESS) {
          throw new Error(data.message);
        }
        setSuccess(`${data.message}\n${data?.issues.join("\n") || ""}`);
        setReloadRequired(true);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    } else {
      alert(message.error.noProductSelected);
    }
  };

  return (
    <>
      <PageTitle pageName={pageName.product.productList} />
      <ProductTopBanner buttonType="add" />
      <Box marginBottom={1}>
        {isMobile ? (
          <>
            <Button
              onClick={(e) => setAnchorElList(e.currentTarget)}
              variant="contained"
            >
              Actions
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={anchorElList}
              open={openList}
              onClose={() => setAnchorElList(null)}
              onClick={() => setAnchorElList(null)}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem onClick={handleTaskButtonClick}>Task</MenuItem>
              {bdc === currentUserId && (
                <div>
                  <MenuItem onClick={handleAssignPMC}>Assign PMC</MenuItem>
                  {unBoundMode && (
                    <MenuItem onClick={handlePMCChecklistButton}>
                      PMC Handover Checklist
                    </MenuItem>
                  )}
                  {unBoundMode && (
                    <MenuItem onClick={handleExecutionTimelineButton}>
                      Execution Timeline
                    </MenuItem>
                  )}
                  {unBoundMode && (
                    <MenuItem onClick={handleInitiateExecutionTimelineButton}>
                      Initiate Timeline
                    </MenuItem>
                  )}
                </div>
              )}
            </Menu>
          </>
        ) : (
          <ButtonGroup variant="contained" aria-label="List Type button group">
            {/* <Button onClick={handleTaskButtonClick}>Task</Button> */}
            {bdc === currentUserId && (
              <Button onClick={handleAssignPMC}>Assign PMC</Button>
            )}
            {bdc === currentUserId && unBoundMode && (
              <Button onClick={handlePMCChecklistButton}>
                PMC Handover Checklist
              </Button>
            )}
            {bdc === currentUserId && unBoundMode && (
              <Button onClick={handleExecutionTimelineButton}>
                Execution Timeline
              </Button>
            )}
            {bdc === currentUserId && unBoundMode && (
              <Button onClick={handleInitiateExecutionTimelineButton}>
                Initiate Timeline
              </Button>
            )}
          </ButtonGroup>
        )}
      </Box>
      <div style={{ width: "100%" }}>
        <ReactTable
          columns={
            isMobile
              ? productListColumnsS({
                  handlePreviewClick,
                  handleUpdateProducts,
                  handleDeleteClick,
                  handleCopyChecklist,
                  handleCopyTimeline,
                  isBdc: bdc === currentUserId,
                  isUnboundMode: unBoundMode,
                  handleUpdateProductExecutionTimeline,
                  handleViewInputOutputClick,
                  handleUpdateProductHandoverChecklist,
                  handleApprovePMCClick,
                  pageAccess,
                  handleInputOutputButtonClick,
                  handleHandoverChecklistButtonClick,
                  handleTimelineButtonClick,
                })
              : productListColumnsL({
                  handlePreviewClick,
                  handleUpdateProducts,
                  handleDeleteClick,
                  handleCopyChecklist,
                  handleCopyTimeline,
                  isBdc: bdc === currentUserId,
                  isUnboundMode: unBoundMode,
                  handleUpdateProductExecutionTimeline,
                  handleViewInputOutputClick,
                  handleUpdateProductHandoverChecklist,
                  handleApprovePMCClick,
                  pageAccess,
                  handleInputOutputButtonClick,
                  handleHandoverChecklistButtonClick,
                  handleTimelineButtonClick,
                })
          }
          data={list}
          selectedRowId={selectedRowId}
          setSelectedRowId={setSelectedRowId}
          getRowStyle={(row) =>
            dayjs(row.lotPartDeadline).diff(
              dayjs(row.executionTimeline.endDate),
              "day",
              true
            ) < 0
              ? { backgroundColor: "#ffbec8ff", borderLeft: "3px solid red" }
              : {}
          }
        />
      </div>
    </>
  );
};
